/**
 * DI-3-S75 §3.1 (arrival order, Christian 2026-10-30 option b) — a set's
 * gimbal `*_pos.txt` files go up FIRST, in a small upload session of their
 * own, and the runner posts `metadata-complete` only once that session's
 * ingest finished: the first geometry run (which `metadata-complete`
 * triggers) then finds the `data_acquisition_files` row.
 *
 *   1. open a session on the set (`openUploadSession` with `purpose: 'aux'`
 *      — the workflow's image-upload mode does not apply to it — and the pos
 *      files' count and bytes as the hint);
 *   2. PUT each file to `data-acquisitions/<da>/aux/<relativeFolder>/<name>`
 *      (`sas.ts:auxBlobUrl` — a sibling of the session's `raw/` prefix under
 *      the same container-scoped `cw` SAS; text/plain, Content-MD5);
 *   3. complete with `{files: [], auxFiles}` (inspection-3/v1 — a pos-only
 *      session);
 *   4. poll `getUploadSession` until it is terminal.
 *
 * Best effort, bounded: any failure (an unreadable file, a PUT failing after
 * its retries, a refused open / complete, a FAILED ingest) or the bound
 * `POS_SESSION_TIMEOUT_MS` passing → the caller goes on WITHOUT the file —
 * the images are never blocked or delayed beyond the bound, and no pilot
 * text. The image sessions carry no `auxFiles` (their body is `{files}`).
 *
 * The PUTs use a queue of their own, not the tab-wide one: that queue is
 * HELD while a set is `completing` (DI-3-S68 B3 "rows before bytes") — which
 * is exactly when this runs.
 */
import type { AuxFile, IndexState } from "../indexStore.js";
import { folderOf } from "../files/dropToFiles.js";
import { md5Hex as md5OfBytes } from "../parse/hashes.js";
import type { AuxManifestEntry } from "../api/uploadSessions.js";
import type { UploadApi } from "./api.js";
import { FileUploadFailed, uploadFile, type FetchLike } from "./blockUpload.js";
import { auxManifestEntry, pollUntilDone, type AuxUpload } from "./manifest.js";
import { UploadQueue } from "./queue.js";
import { SasSource, auxBlobUrl } from "./sas.js";

/** The bound on the whole pos-only session (open → PUTs → complete → ingest). */
export const POS_SESSION_TIMEOUT_MS = 30_000;

/** The pos session's poll interval (a single small file: faster than the images' 5 s). */
export const POS_POLL_MS = 1_000;

/**
 * The `*_pos.txt` files of a set: those lying in a folder its member images
 * come from (the images' `relativePath` folder), in the index's order; on
 * the wire under their folder inside the dropped folder (`relativeFolder`,
 * `''` = the dropped folder itself). None → [].
 */
export function auxFilesOf(
  rows: IndexState["rows"],
  memberIndexes: readonly number[],
  aux: readonly AuxFile[],
): AuxUpload[] {
  if (aux.length === 0 || memberIndexes.length === 0) return [];
  const folders = new Set<string>();
  for (const i of memberIndexes) {
    const r = rows[i];
    if (r) folders.add(folderOf(r.relativePath));
  }
  return aux.flatMap((a) =>
    folders.has(folderOf(a.relativePath))
      ? [{ filename: a.name, relativeFolder: a.relativeFolder, file: a.file }]
      : [],
  );
}

/** A Blob's bytes (`FileReader` where `Blob.arrayBuffer` is missing — jsdom). */
function bytesOf(b: Blob): Promise<ArrayBuffer> {
  if (typeof b.arrayBuffer === "function") return b.arrayBuffer();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      resolve(r.result as ArrayBuffer);
    };
    r.onerror = () => {
      reject(r.error ?? new Error("not readable"));
    };
    r.readAsArrayBuffer(b);
  });
}

async function defaultMd5OfBlob(b: Blob): Promise<string> {
  return md5OfBytes(new Uint8Array(await bytesOf(b)));
}

export interface PosSessionInput {
  daId: string;
  files: readonly AuxUpload[];
  api: Pick<UploadApi, "open" | "complete" | "poll">;
  fetch?: FetchLike | undefined;
  sleep?: ((ms: number) => Promise<void>) | undefined;
  now?: (() => number) | undefined;
  pollMs?: number | undefined;
  timeoutMs?: number | undefined;
  md5OfBlob?: ((b: Blob) => Promise<string>) | undefined;
}

/**
 * How the pos session ended (for the tests and the console — never shown):
 * `none` no file; `done` the ingest COMPLETED; `failed` anything refused or
 * failed; `timeout` the bound passed (the session may still finish later).
 */
export type PosSessionOutcome = "none" | "done" | "failed" | "timeout";

const defaultSleep = (ms: number) =>
  new Promise<void>((r) => {
    setTimeout(r, ms);
  });

async function run(input: PosSessionInput): Promise<PosSessionOutcome> {
  const sleep = input.sleep ?? defaultSleep;
  const sas = new SasSource(
    () =>
      input.api.open(input.daId, {
        purpose: "aux",
        expectedFileCount: input.files.length,
        expectedTotalBytes: input.files.reduce((n, f) => n + f.file.size, 0),
      }),
    input.now ?? (() => Date.now()),
  );
  const queue = new UploadQueue(2, input.now ?? (() => Date.now()), null);
  const entries: AuxManifestEntry[] = [];
  for (const [k, a] of input.files.entries()) {
    let md5: string;
    try {
      md5 = await (input.md5OfBlob ?? defaultMd5OfBlob)(a.file);
    } catch {
      console.warn(`[intake] ${a.filename}: not readable — not sent`);
      continue;
    }
    const opened = await sas.get();
    try {
      await uploadFile({
        file: a.file,
        filename: a.filename,
        md5Hex: md5,
        blockSize: opened.blockSize,
        url: (sasUrl) => auxBlobUrl(sasUrl, a.relativeFolder, a.filename),
        contentType: "text/plain",
        sasUrl: async () => (await sas.get()).url,
        onForbidden: (url) => {
          sas.invalidate(url);
        },
        queue,
        key: [0, k],
        ...(input.fetch ? { fetch: input.fetch } : {}),
        sleep,
      });
    } catch (e) {
      if (!(e instanceof FileUploadFailed)) throw e;
      console.warn(`[intake] ${a.filename}: ${e.message} — not sent`);
      continue;
    }
    entries.push(auxManifestEntry(a, a.file.size, md5));
  }
  if (entries.length === 0) return "failed";
  const session = await sas.get();
  await input.api.complete(input.daId, session.sessionId, [], entries);
  const outcome = await pollUntilDone({
    poll: () => input.api.poll(input.daId, session.sessionId),
    sleep,
    intervalMs: input.pollMs ?? POS_POLL_MS,
  });
  return outcome.state === "COMPLETED" ? "done" : "failed";
}

/**
 * Send the set's pos files in their own session and wait for its ingest —
 * at most `timeoutMs` (default `POS_SESSION_TIMEOUT_MS`). Never throws.
 */
export async function sendPosSession(
  input: PosSessionInput,
): Promise<PosSessionOutcome> {
  if (input.files.length === 0) return "none";
  let timer: ReturnType<typeof setTimeout> | undefined;
  const bound = new Promise<PosSessionOutcome>((resolve) => {
    timer = setTimeout(() => {
      resolve("timeout");
    }, input.timeoutMs ?? POS_SESSION_TIMEOUT_MS);
  });
  const work = run(input).catch((e: unknown) => {
    console.warn(
      `[intake] the position file was not sent (${e instanceof Error ? e.message : String(e)})`,
    );
    return "failed" as const;
  });
  try {
    const outcome = await Promise.race([work, bound]);
    if (outcome !== "done")
      console.warn(`[intake] position file session: ${outcome}`);
    return outcome;
  } finally {
    clearTimeout(timer);
  }
}
